import React, { useEffect, useState } from 'react';
import { useLocation, Link, useParams } from 'react-router-dom';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';
import useApiService from '../Services/ApiService';
import './Breadcrumbs.css';
import { useAuth } from '../Contexts/AuthContext';
import CONFIG from "../Services/CONFIG";

const Breadcrumbs = () => {
    const { get: get_channel } = useApiService(CONFIG.SERVER_CHANNEL);
    const { get: get_organization } = useApiService(CONFIG.SERVER_ORGANIZATION);
    const { get: get_assistant } = useApiService(CONFIG.SERVER_ASSISTANT);
    const { get: get_document } = useApiService(CONFIG.SERVER_DOCUMENT);

    const location = useLocation();

    const {
        organizationId,
        promptId,
        agentId,
        notificationId,
        documentId,
        channelId,
        agentFrameId,
        collectionId,
        dictionaryId,
        thematicId,
        apiKeyId,
        artifactId,
        connectionId,
        fragmentId,
        connectionObjectId,
        userId,
        markerId,
        configId,
    } = useParams();

    const { getOrganizationById } = useOrganizationApi();
    const [organization, setOrganization] = useState(null);
    const [agent, setAgent] = useState(null);
    const [channel, setChannel] = useState(null);
    const [prompt, setPrompt] = useState(null);
    const [document, setDocument] = useState(null);
    const [notification, setNotification] = useState(null);
    const [agentFrame, setAgentFrame] = useState(null);
    const [documentCollection, setDocumentCollection] = useState(null);
    const [taxonomyDictionary, setTaxonomyDictionary] = useState(null);
    const [thematic, setThematic] = useState(null);
    const [apiKey, setApiKey] = useState(null);
    const [artifact, setArtifact] = useState(null);
    const [connection, setConnection] = useState(null);
    const [fragment, setFragment] = useState(null);
    const [connectionObject, setConnectionObject] = useState(null);
    const [user, setUser] = useState(null);
    const [marker, setMarker] = useState(null);
    const [postCallAnalysisConfig, setPostCallAnalysisConfig] = useState(null);

     // Get user from auth context
    const { status } = useAuth();
    // Access user permissions safely
    const permissions = status.permissions || [];

    // Fetch organization title asynchronously if organizationId exists
    useEffect(() => {
        const fetchOrganization = async () => {
            if (organizationId) {
                try {
                    const org = await getOrganizationById(organizationId);
                    setOrganization(org);
                } catch (error) {
                    console.error('Error fetching organization:', error);
                }
            }
        };

        fetchOrganization();
    }, [organizationId, getOrganizationById]);

    useEffect(() => {
        const fetchAgent = async () => {
            if (agentId && agentId !== 'new-agent') {
                try {
                    const agent = await get_assistant(`/v1/assistants/${agentId}`);
                    setAgent(agent.data);
                } catch (error) {
                    console.error('Error fetching agent:', error);
                }
            }
        };
        fetchAgent();
    }, [agentId]);

    useEffect(() => {
        const fetchNotification = async () => {
            if (notificationId && notificationId !== 'new-notification') {
                try {
                    const notification = await get_assistant(`/v1/notifications/${notificationId}`)
                    setNotification(notification.data);
                } catch (error) {
                    console.error('Error fetching notification:', error);
                }
            }
        };
        fetchNotification();
    }, [notificationId]);
    useEffect(() => {
        const fetchPrompt = async () => {
            if (promptId && promptId !== 'new-prompt') {
                try {
                    const prompt = await get_assistant(`/v1/prompts/${promptId}`);
                    setPrompt(prompt.data);
                } catch (error) {
                    console.error('Error fetching prompt:', error);
                }
            }
        };
        fetchPrompt();
    }, [promptId]);
    useEffect(() => {
        const fetchDocument = async () => {
            if (documentId) {
                try {
                    const document = await get_document(`/v1/documents/${documentId}`);
                    setDocument(document.data);
                } catch (error) {
                    console.error('Error fetching document:', error);
                }
            }
        };
        fetchDocument();
    }, [documentId]);
    useEffect(() => {
        const fetchChannel = async () => {
            if (channelId && channelId !== 'new-channel') {
                try {
                    const channel = await get_channel(`/v1/channels/${channelId}`);
                    setChannel(channel);
                } catch (error) {
                    console.error('Error fetching channel:', error);
                }
            }
        };
        fetchChannel();
    }, [channelId]);

    // Fetch Agent Frame (for final breadcrumb)
    useEffect(() => {
        const fetchAgentFrame = async () => {
            if (agentFrameId && agentFrameId !== 'new-agent-frame') {
                try {
                    const res = await get_assistant(`/v1/assistant_frames/${agentFrameId}`);
                    setAgentFrame(res.data);
                } catch (error) {
                    console.error('Error fetching agent frame:', error);
                }
            }
        };
        fetchAgentFrame();
    }, [agentFrameId]);

    useEffect(() => {
        const fetchDocumentCollection = async () => {
            if (collectionId && collectionId !== 'new-collection') {
                try {
                    const res = await get_document(`/v1/document_collection/${collectionId}`);
                    setDocumentCollection(res.data);
                } catch (error) {
                    console.error('Error fetching collection:', error);
                }
            }
        };
        fetchDocumentCollection();
    }, [collectionId]);

    useEffect(() => {
        const fetchTaxonomyDictionary = async () => {
            if (dictionaryId && dictionaryId !== 'new-dictionary') {
                try {
                    const res = await get_assistant(`/v1/taxonomy_dictionary/${dictionaryId}`);
                    setTaxonomyDictionary(res.data);
                } catch (error) {
                    console.error('Error fetching taxonomy dictionary:', error);
                }
            }
        };
        fetchTaxonomyDictionary();
    }, [dictionaryId]);

    useEffect(() => {
        const fetchThematic = async () => {
            if (thematicId && thematicId !== 'new-thematic') {
                try {
                    const res = await get_channel(`/v1/thematics/${thematicId}`);
                    setThematic(res.data);
                } catch (error) {
                    console.error('Error fetching thematic:', error);
                }
            }
        };
        fetchThematic();
    }, [thematicId]);

    useEffect(() => {
        const fetchApiKey = async () => {
            if (apiKeyId && apiKeyId !== 'new-api-key') {
                try {
                    const res = await get_organization(`/v1/api-keys/${apiKeyId}`);
                    setApiKey(res.data);
                } catch (error) {
                    console.error('Error fetching api key:', error);
                }
            }
        };
        fetchApiKey();
    }, [apiKeyId]);

    useEffect(() => {
        const fetchArtifact = async () => {
            if (artifactId && artifactId !== 'new-artifact') {
                try {
                    const res = await get_organization(`/v1/artifacts/${artifactId}`);
                    setArtifact(res.data);
                } catch (error) {
                    console.error('Error fetching artifact:', error);
                }
            }
        };
        fetchArtifact();
    }, [artifactId]);

    useEffect(() => {
        const fetchConnections = async () => {
            if (connectionId && connectionId !== 'new-connection') {
                try {
                    const res = await get_organization(`/v1/connections/${connectionId}`);
                    setConnection(res.data);
                } catch (error) {
                    console.error('Error fetching connection:', error);
                }
            }
        };
        fetchConnections();
    }, [connectionId]);

    useEffect(() => {
        const fetchFragment = async () => {
            if (fragmentId && fragmentId !== 'new-fragment') {
                try {
                    const res = await get_channel(`/v1/fragments/${fragmentId}`);
                    setFragment(res.data);
                } catch (error) {
                    console.error('Error fetching fragment:', error);
                }
            }
        };
        fetchFragment();
    }, [fragmentId]);

    useEffect(() => {
        const fetchConnectionObject = async () => {
            if (connectionObjectId && connectionObjectId !== 'new-connection-object') {
                try {
                    const res = await get_organization(`/v1/connection_objects/${connectionObjectId}`);
                    setConnectionObject(res.data);
                } catch (error) {
                    console.error('Error fetching connection object:', error);
                }
            }
        };
        fetchConnectionObject();
    }, [connectionObjectId]);

    useEffect(() => {
        const fetchUser = async () => {
            if (userId && userId !== 'new-user') {
                try {
                    const res = await get_organization(`/v1/users/${userId}`);
                    setUser(res.data);
                } catch (error) {
                    console.error('Error fetching user:', error);
                }
            }
        };
        fetchUser();
    }, [userId]);

    // Fetch Marker (for final breadcrumb)
    useEffect(() => {
        const fetchMarker = async () => {
            if (markerId && markerId !== 'new-marker') {
                try {
                    const res = await get_assistant(`/v1/markers/${markerId}`);
                    setMarker(res.data);
                } catch (error) {
                    console.error('Error fetching marker:', error);
                }
            }
        };
        fetchMarker();
    }, [markerId]);

    // Fetch Post Call Analysis Config (for final breadcrumb)
    useEffect(() => {
        const fetchPostCallAnalysisConfig = async () => {
            if (configId && configId !== 'new') {
                try {
                    const res = await get_channel(`/v1/post-call-analysis/${configId}`);
                    setPostCallAnalysisConfig(res.data);
                } catch (error) {
                    console.error('Error fetching post-call analysis config:', error);
                }
            }
        };
        fetchPostCallAnalysisConfig();
    }, [configId]);


    // Split the path into parts
    const pathParts = location.pathname.split('/').filter((part) => part);

    

    const breadcrumbs = pathParts.map((part, index) => {
        // If the part is "organizations", check the permissions.
        if (part.toLowerCase() === 'organizations' && !(permissions.includes('all:organizations') || permissions.includes('read:organizations'))) {
            return null; // Skip rendering this breadcrumb item
        }
        
        let isLast = index === pathParts.length - 1;
        let pathUpToPart = `/${pathParts.slice(0, index + 1).join('/')}`;
        
        // Special handling for organizationId, agentId, etc.
        if (part === organizationId && organization) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {organization.title || 'Organization'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {organization.title || 'Organization'}
                </Link>
            );
        }
        if (part === agentId && agent) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {agent.name}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {agent.name}
                </Link>
            );
        }
        if (part === notificationId && notification) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {notification.name  || 'Notification'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {notification.name || 'Notification'}
                </Link>
            );
        }
        if (part === promptId && prompt) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {prompt.name || 'Prompt'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {prompt.name || 'Prompt'}
                </Link>
            );
        }
        if (part === documentId && document) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {document.original_name || 'Document'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {document.original_name || 'Document'}
                </Link>
            );
        }
        if (part === channelId && channel) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {channel.name || 'Channel'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {channel.name || 'Channel'}
                </Link>
            );
        }
        
        // Agent Frame breadcrumb (final node shows frame name)
        if (part === agentFrameId && agentFrame) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {agentFrame.name || 'Frame'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {agentFrame.name || 'Frame'}
                </Link>
            );
        }

        if (part === collectionId && documentCollection) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {documentCollection.name || 'Document Collection'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {documentCollection.name || 'Document Collection'}
              </Link>
            );
        }

        if (part === dictionaryId && taxonomyDictionary) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {taxonomyDictionary.name || 'Taxonomy Dictionary'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {taxonomyDictionary.name || 'Taxonomy Dictionary'}
              </Link>
            );
        }

        if (part === thematicId && thematic) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {thematic.name || 'Thematic'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {thematic.name || 'Thematic'}
              </Link>
            );
        }

        if (part === apiKeyId && apiKey) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {apiKey.name || 'Api key'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {apiKey.name || 'Api key'}
              </Link>
            );
        }

        if (part === artifactId && artifact) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {artifact.name || 'artifact'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {artifact.name || 'artifact'}
              </Link>
            );
        }

        if (part === connectionId && connection) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {connection.name || 'connection'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {connection.name || 'connection'}
              </Link>
            );
        }

        if (part === fragmentId && fragment) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {fragment.name || 'fragment'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {fragment.name || 'fragment'}
              </Link>
            );
        }

        if (part === connectionObjectId && connectionObject) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {connectionObject.name || 'connection object'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {connectionObject.name || 'connection object'}
              </Link>
            );
        }

        if (part === userId && user) {
            return isLast ? (
              <span key={index} className="breadcrumb-item">
                    {user.name || 'user'}
                </span>
            ) : (
              <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                  {user.name || 'user'}
              </Link>
            );
        }

        // Marker breadcrumb (final node shows marker name)
        if (part === markerId && marker) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {marker.name || 'Marker'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {marker.name || 'Marker'}
                </Link>
            );
        }

        // Post Call Analysis Config breadcrumb (final node shows config name)
        if (part === configId && postCallAnalysisConfig) {
            return isLast ? (
                <span key={index} className="breadcrumb-item">
                    {postCallAnalysisConfig.name || 'Configuration'}
                </span>
            ) : (
                <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                    {postCallAnalysisConfig.name || 'Configuration'}
                </Link>
            );
        }

        // Handle "new" for post-call-analysis config
        if (part === 'new' && configId === 'new') {
            return (
                <span key={index} className="breadcrumb-item">
                    New Configuration
                </span>
            );
        }

        // Change the URL of "Channel" but keep its label unchanged
        if (part.toLowerCase() === 'channel') {
            return (
                <Link key={index} to={`/organizations/${organizationId}/chats`} className="breadcrumb-link">
                    {capitalize(part.replace(/-/g, ' '))}
                </Link>
            );
        }
        
        // Remove chat ID numbers from breadcrumbs
        if (!isNaN(part)) return null;
        
        // Handle other breadcrumb items
        return isLast ? (
            <span key={index} className="breadcrumb-item">
                {capitalize(part.replace(/-/g, ' '))}
            </span>
        ) : (
            <Link key={index} to={pathUpToPart} className="breadcrumb-link">
                {capitalize(part.replace(/-/g, ' '))}
            </Link>
        );
    });

    return (
        <div className="breadcrumbs">
            {breadcrumbs.map((crumb, index) =>
                crumb ? (
                    <React.Fragment key={index}>
                        {crumb}
                        {index < breadcrumbs.length - 1 && (
                            <span className="breadcrumb-separator">
                                <i className="fa-solid fa-chevron-right"></i>
                            </span>
                        )}
                    </React.Fragment>
                ) : null
            )}
        </div>
    );
};

// Utility to capitalize the first letter of a word
const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);

export default Breadcrumbs;
